import React, { lazy, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'

import Nfts from '../components/nft/Nfts'
import {
  changeNftsState,
  createNft,
  getNfts,
  getNftUsers,
  redeemNftExclusiveContent,
  resetNftsState,
  transferNft,
  updateNft,
  updateSelectedNft
} from '../store/actions/nfts'
import {
  DEFAULT_CONTAINER_STATE,
  NFTS_PROCESS_STEP
} from '../constants/containerStates'
import CreateNftForm from '../components/nft/CreateNftForm'
import { SETUP_URL } from '../constants/navigation'
import { buildMetadataAttributes } from '../util/account.helpers'
import { updateUserAccount } from '../store/actions/account'
import {
  checkValidations,
  requiredRule,
  transWithArgs
} from '../util/validation.helpers'
import { containsNoValues, containsValues } from '../util/object.helpers'
import { buildNftValidationRules } from '../util/nft.helpers'
import { NFTS_PROFILE_DISPATCH } from '../store/reducers/nfts/nfts'
import { NFT_YES_NO_OPTIONS } from '../data/nfts'
import NftDetails from '../components/popups/nfts/NftDetails'
import { QB_INPUT_EMAIL } from '../constants/input'
import Input from '../components/shared/inputs/Input'
import { getTokens } from '../store/actions/token'

const ConfirmationPopup = lazy(() =>
  import('../components/popups/common/Confirmation')
)

const ConfirmedPopup = lazy(() =>
  import('../components/popups/common/Confirmed')
)

const NftsContainer = ({
  tokenProfile,
  nftsProfile,
  dashboardSetupState,
  changeNftsState,
  updateUserAccount,
  createNft,
  resetNftsState,
  history,
  brandName,
  getNfts,
  nftsListProfile,
  updateNft,
  getNftUsers,
  nftUsersProfile,
  redeemNftExclusiveContent,
  updateSelectedNft,
  transferNft,
  getTokens,
  ...restProps
}) => {
  const { t } = useTranslation()
  const { nftPopupState, media, success, error, transferId, userSale } =
    nftsProfile
  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)
  const [showNft, setShowNft] = useState()
  const [nftRedeemDetails, setNftRedeemDetails] = useState({})
  const { selectedNft } = nftsListProfile

  useEffect(() => {
    const action = nftPopupState
    if (action) {
      buildSuccessPopup(action)
    }
  }, [nftPopupState])

  useEffect(() => {
    if (userSale === NFT_YES_NO_OPTIONS.no) {
      changeNftsState(NFTS_PROFILE_DISPATCH.USER_SALES_FEE, '')
    }
  }, [userSale])

  useEffect(() => {
    getNfts()
    getTokens()
  }, [])

  const handleGoToSetup = () => {
    history.replace(SETUP_URL)
  }

  const handleCloseImportantNote = () => {
    updateUserAccount(buildMetadataAttributes({ hide_nft_note: true }))
  }

  const handleCreateNewNft = () => {
    setPopupState(NFTS_PROCESS_STEP.CREATE_NEW_NFT)
  }

  const handleNftDetailsClick = (nft) => {
    getNftUsers({ tokenId: nft.id, pageNumber: 0 })
    updateSelectedNft(nft)
    setShowNft(nft.canRedeem)
    setPopupState(NFTS_PROCESS_STEP.NFT_DETAILS)
  }

  const handleChangeNftUsersPage = (pageNumber) => {
    getNftUsers({ tokenId: selectedNft?.id, pageNumber })
  }

  const handleNftUsersPerPageChange = (recordsPerPage) => {
    getNftUsers({ tokenId: selectedNft?.id, pageNumber: 0, recordsPerPage })
  }

  const handleRedeemNftExclusiveContent = (
    authId,
    tokenId,
    nftId,
    claimCount
  ) => {
    setPopupState(NFTS_PROCESS_STEP.REDEEM_EXCLUSIVE_CONTENT)
    setNftRedeemDetails({ authId, tokenId, nftId, claimCount })
  }

  const handleSubmitRedeemExclCnt = () => {
    const { authId, tokenId, nftId, claimCount } = nftRedeemDetails
    redeemNftExclusiveContent(authId, tokenId, nftId, claimCount)
    handleOnCancelPopup()
  }

  const handleTransferNft = () => {
    setPopupState(NFTS_PROCESS_STEP.TRANSFER_NFT)
  }

  const handleTransferNftSubmit = () => {
    const errors = checkValidations({
      transferId: { value: transferId, rules: [requiredRule] }
    })
    if (containsNoValues(errors)) {
      transferNft(transferId, selectedNft.id)
    } else {
      changeNftsState(NFTS_PROFILE_DISPATCH.ERROR, errors)
    }
  }

  const handleShowNftChange = (tokenId, event) => {
    setShowNft(event.target.value)
    updateNft(tokenId, event.target.value)
  }

  const handleNoPopup = () => {
    resetNftsState()
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handleOnCancelPopup = () => {
    switch (popupState) {
      case NFTS_PROCESS_STEP.TRANSFER_NFT:
      case NFTS_PROCESS_STEP.REDEEM_EXCLUSIVE_CONTENT:
        setPopupState(NFTS_PROCESS_STEP.NFT_DETAILS)
        break

      default:
        handleNoPopup()
    }
  }

  const handleOnSubmitPopup = () => {
    switch (popupState) {
      case NFTS_PROCESS_STEP.CREATE_NEW_NFT:
        const nftErrors = checkValidations(buildNftValidationRules(nftsProfile))
        if (containsValues(nftErrors)) {
          changeNftsState(NFTS_PROFILE_DISPATCH.ERROR, nftErrors)
        } else {
          createNft(media)
        }
        break

      case NFTS_PROCESS_STEP.NFT_CREATED:
      case NFTS_PROCESS_STEP.FILE_TYPE_NOT_SUPPORTED:
      case DEFAULT_CONTAINER_STATE.SOMETHING_WENT_WRONG:
        handleNoPopup()
        break

      case NFTS_PROCESS_STEP.NFT_TRANSFERRED:
        setPopupState(NFTS_PROCESS_STEP.NFT_DETAILS)
        break

      default:
        break
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case NFTS_PROCESS_STEP.CREATE_NEW_NFT:
        return (
          <CreateNftForm
            t={t}
            nftsProfile={nftsProfile}
            tokenProfile={tokenProfile}
            onClose={handleOnCancelPopup}
            onSubmit={handleOnSubmitPopup}
            dispatchAction={changeNftsState}
            title={t('nft.create-new-nft')}
          />
        )

      case NFTS_PROCESS_STEP.NFT_CREATED:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            title='common.congratulations'
            description={t('nft.nft-created-desc')}
          />
        )

      case NFTS_PROCESS_STEP.FILE_TYPE_NOT_SUPPORTED:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            title='nft.file-type-not-supported'
            description='nft.try-again-types-list'
          />
        )

      case NFTS_PROCESS_STEP.NFT_DETAILS:
        return (
          <NftDetails
            onClose={handleOnCancelPopup}
            onSubmit={handleTransferNft}
            nft={selectedNft}
            brandName={brandName}
            tokenProfile={tokenProfile}
            onShowNftChange={handleShowNftChange}
            showNft={showNft}
            nftUsersProfile={nftUsersProfile}
            onChangeNftUsersPage={handleChangeNftUsersPage}
            onNftUsersPerPageChange={handleNftUsersPerPageChange}
            onRedeemNftExclusiveContent={handleRedeemNftExclusiveContent}
            t={t}
          />
        )

      case NFTS_PROCESS_STEP.TRANSFER_NFT:
        return (
          <ConfirmationPopup
            t={t}
            onSubmit={handleTransferNftSubmit}
            onClose={handleOnCancelPopup}
            title='nft.transfer-nft'
            description='nft.transfer-nft-desc'
            cancelLabel='common.cancel'
            submitLabel='common.transfer'
            banner={
              <Grid item xs={12} sx={{ px: 4 }}>
                <Input
                  id={NFTS_PROFILE_DISPATCH.TRANSFER_ID}
                  label={t('nft.receiver-auth-id')}
                  value={transferId}
                  errorMessage={transWithArgs(
                    t,
                    error[NFTS_PROFILE_DISPATCH.TRANSFER_ID]
                  )}
                  onChange={(value) =>
                    changeNftsState(NFTS_PROFILE_DISPATCH.TRANSFER_ID, value)
                  }
                  successMsg={transWithArgs(
                    t,
                    success[NFTS_PROFILE_DISPATCH.TRANSFER_ID]
                  )}
                  type={QB_INPUT_EMAIL}
                  size='small'
                />
              </Grid>
            }
          />
        )

      case NFTS_PROCESS_STEP.REDEEM_EXCLUSIVE_CONTENT:
        return (
          <ConfirmationPopup
            t={t}
            onSubmit={handleSubmitRedeemExclCnt}
            title='nft.change-excl-cnt-status'
            description='nft.change-excl-cnt-status-desc'
            onClose={handleOnCancelPopup}
            cancelLabel='common.cancel'
            submitLabel='common.confirm'
          />
        )

      case DEFAULT_CONTAINER_STATE.SOMETHING_WENT_WRONG:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            title='error.ooops'
            description='error.it-looks-like-something-wrong'
          />
        )

      case NFTS_PROCESS_STEP.NFT_TRANSFERRED:
        return (
          <ConfirmedPopup
            t={t}
            onSubmit={handleOnSubmitPopup}
            title='common.congratulations'
            description='nft.transfer-successful-desc'
          />
        )

      default:
        break
    }
  }

  const buildSuccessPopup = (action) => {
    setPopupState(action)
  }

  return (
    <>
      <Nfts
        {...restProps}
        onCloseImportantNote={handleCloseImportantNote}
        t={t}
        tokenProfile={tokenProfile}
        nftsListProfile={nftsListProfile}
        dashboardSetupState={dashboardSetupState}
        dispatchAction={changeNftsState}
        onCreateNewNft={handleCreateNewNft}
        onClickSetup={handleGoToSetup}
        brandName={brandName}
        onNftDetailsClick={handleNftDetailsClick}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({
  dashboardReducer,
  tokensReducer,
  nftsReducer,
  accountReducer
}) => {
  const { dashboardSetupProfile, brandProfile } = dashboardReducer
  const { tokenProfile } = tokensReducer
  const { nftsProfile, nftsListProfile, nftUsersProfile } = nftsReducer
  return {
    dashboardSetupState: dashboardSetupProfile.dashboardSetupState,
    hideNftNote: accountReducer.accountProfile.metadata?.hideNftNote,
    tokenProfile,
    nftsProfile,
    nftsListProfile,
    nftUsersProfile,
    brandName: brandProfile.name
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeNftsState,
      updateUserAccount,
      createNft,
      resetNftsState,
      getNfts,
      updateNft,
      getNftUsers,
      redeemNftExclusiveContent,
      updateSelectedNft,
      transferNft,
      getTokens
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(NftsContainer)
